'use client';
import { useCallback, useMemo } from 'react';
import {
  filterQueryParam,
  filterQueryParams,
  encodeFilterValues,
  readFilterSelection,
} from './filterQueryParams';
import type { FilterCategory, FilterSelection } from './productFilterTypes';
import { usePanelQueryParams } from './usePanelQueryParams';

/** A stable identity, so an unfiltered page does not re-memoise every render. */
const NO_SELECTION: FilterSelection = {};

export type UseProductFilterResult = {
  /** What the URL currently describes, narrowed to the offered options. */
  selection: FilterSelection;
  /** Ticks or unticks one option in one group. */
  toggleValue: (categoryId: string, value: string) => void;
  /** Drops every group's parameter in a single history write. */
  clearAll: () => void;
  activeCount: number;
  /** False until the URL has been read, on the render after hydration. */
  applied: boolean;
};

/**
 * The selection is derived from the URL rather than held in state, for the same
 * reason the sort is: the table and the cards cannot drift, and a deep link
 * takes the same path as a click.
 */
export const useProductFilter = (
  categories: readonly FilterCategory[],
): UseProductFilterResult => {
  const { readParam, paramsKey, write } = usePanelQueryParams(
    filterQueryParams(categories),
  );

  const selection = useMemo(
    () =>
      paramsKey === null
        ? NO_SELECTION
        : readFilterSelection(categories, readParam),
    // `readParam` closes over `searchParams`, which `paramsKey` already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, paramsKey],
  );

  const toggleValue = useCallback(
    (categoryId: string, value: string) => {
      const param = filterQueryParam(categoryId);
      write((params) => {
        const current = new Set(selection[categoryId] ?? []);
        if (current.has(value)) current.delete(value);
        else current.add(value);

        // Dropped rather than left empty, so a cleared group is
        // indistinguishable from one the reader never touched.
        if (current.size === 0) params.delete(param);
        else params.set(param, encodeFilterValues([...current]));
      });
    },
    [selection, write],
  );

  const clearAll = useCallback(() => {
    const params = filterQueryParams(categories);
    write((search) => params.forEach((param) => search.delete(param)));
  }, [categories, write]);

  const activeCount = useMemo(
    () =>
      Object.values(selection).reduce(
        (total, values) => total + values.length,
        0,
      ),
    [selection],
  );

  return {
    selection,
    toggleValue,
    clearAll,
    activeCount,
    applied: paramsKey !== null,
  };
};
